import { useEffect, useRef, useState } from "react";
import useInView from "./useInView";

/**
 * Drives a @penwell/draw-on animation so it plays the first time its container
 * scrolls into view, rather than finishing above the fold before anyone sees
 * it. Replays whenever the markup changes, which is how a theme switch redraws
 * the sketch in the new ink.
 *
 * The library reports "drawing" as soon as it mounts the markup, so the phase
 * returned here is the component's own and starts at "idle".
 *
 * Returns [ref, phase, setPhase].
 */
const useDrawOnceInView = (markup, { play, isError = false, threshold = 0.3 } = {}) => {
  const [ref, inView] = useInView({ threshold });
  const [phase, setPhase] = useState("idle");
  const playedFor = useRef(null);

  useEffect(() => {
    setPhase("idle");
  }, [markup]);

  useEffect(() => {
    if (!inView || isError || !play || playedFor.current === markup) return;

    playedFor.current = markup;
    setPhase("drawing");
    play();
  }, [inView, isError, markup, play]);

  return [ref, phase, setPhase];
};

export default useDrawOnceInView;
